import React, { useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Tooltip, { TooltipProps } from 'core/elements/tooltip/Tooltip'
import { topMiddle } from 'core/elements/menu/defaults'
import Text from 'core/elements/Text'
import { lightenDarkenColor, memoize } from 'utils/misc'
import generateTestId from 'utils/test-helpers'
import clsx from 'clsx'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'

export type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'unknown'
  | 'danger'
  | 'error'
interface BadgeProps {
  text: string | React.ReactNode
  icon?: string | React.ReactNode
  additionalText?: string
  variant: BadgeVariant
  ellipsisAt?: number
  canDismissEllipsis?: boolean
  bold?: boolean
  tooltipBody?: React.ReactNode
  tooltipProps?: Partial<TooltipProps>
  className?: string
  overlayComponent?: React.ReactNode
  hideTooltip?: boolean
  onClick?: (e: React.MouseEvent) => void
  solid?: boolean
  spin?: boolean
}
const defaultTooltipProps = {
  ...topMiddle,
  origin: 'right bottom',
}

const defaultVariant = 'default'

const getTooltipProps = memoize((tooltipBody, text, hideTooltip, shouldTruncateText) => {
  if (hideTooltip) {
    return {}
  }
  if (tooltipBody && typeof tooltipBody !== 'string') return { customBody: tooltipBody }
  return { message: tooltipBody ?? (shouldTruncateText ? text : undefined) }
})

//TODO: add badge component here.

export default function BadgeWithIcon({
  text = '',
  icon,
  additionalText,
  variant = defaultVariant,
  ellipsisAt = 15,
  canDismissEllipsis = false,
  bold = true,
  tooltipBody = undefined,
  tooltipProps = defaultTooltipProps,
  className,
  overlayComponent,
  hideTooltip = false,
  onClick,
  solid = true,
  spin = false,
}: BadgeProps) {
  const classes = useStyles()
  const [showAll, setShowAll] = useState(false)
  const shouldTruncateText =
    !showAll &&
    typeof text === 'string' &&
    text?.length > ellipsisAt &&
    ellipsisAt !== null &&
    ellipsisAt !== undefined
  const textToShow = shouldTruncateText ? (text as string)?.substring(0, ellipsisAt) : text
  return (
    <Tooltip
      {...tooltipProps}
      {...getTooltipProps(tooltipBody, text, hideTooltip, shouldTruncateText)}
    >
      <div
        className={clsx(
          classes.badge,
          classes[`badge${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
          className,
        )}
        onClick={onClick}
      >
        {icon && (
          <FontAwesomeIcon
            size="md"
            className={clsx(
              'badgeIcon',
              classes.badgeIconContainer,
              classes[`badgeIconContainer${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
            )}
            solid={solid}
            spin={spin}
          >
            {icon}
          </FontAwesomeIcon>
        )}
        <Text
          data-testid={generateTestId(textToShow)}
          variant={bold ? 'caption1' : 'body2'}
          component="span"
          className={clsx(
            'badgeText',
            classes.badgeText,
            classes[`badgeText${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
          )}
        >
          {textToShow}
          {shouldTruncateText && '...'}
        </Text>
        {additionalText && (
          <Text
            data-testid={generateTestId(additionalText)}
            variant={bold ? 'caption1' : 'body2'}
            className={clsx(
              'additionalText',
              classes.additionalText,
              classes[`additionalText${variant.charAt(0).toUpperCase() + variant.slice(1)}`],
            )}
          >
            {additionalText}
          </Text>
        )}

        {canDismissEllipsis && (
          <div className={classes.showMore} onClick={() => setShowAll(!showAll)}>
            <Text className={classes.showMoreText} variant="caption1">
              {showAll ? 'Show Less' : 'Show More'}
            </Text>
          </div>
        )}
        {overlayComponent}
      </div>
    </Tooltip>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  badge: {
    width: 'max-content',
    borderRadius: 4,
    padding: '2px 8px 4px 8px',
    whiteSpace: 'nowrap',
  },
  badgeText: {},
  additionalText: {
    display: 'inline-table',
    margin: '-2px -8px -4px 8px',
  },
  badgeIconContainer: {
    marginRight: theme.spacing(1),
  },
  // Variant classes
  badgeDefault: {
    color: theme.components.badge?.default?.color,
    backgroundColor: theme.components.badge?.default?.background,
    border: `1px solid ${theme.components.badge?.default?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.default?.color}` },
  },
  badgePrimary: {
    color: theme.components.badge?.primary?.color,
    backgroundColor: theme.components.badge?.primary?.background,
    border: `1px solid ${theme.components.badge?.primary?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.primary?.color}` },
  },
  badgeSecondary: {
    color: theme.components.badge?.secondary?.color,
    backgroundColor: theme.components.badge?.secondary?.background,
    border: `1px solid ${theme.components.badge?.secondary?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.secondary?.color}` },
  },
  badgeSuccess: {
    color: theme.components.badge?.success?.color,
    backgroundColor: theme.components.badge?.success?.background,
    border: `1px solid ${theme.components.badge?.success?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.success?.color}` },
  },
  badgeWarning: {
    color: theme.components.badge?.warning?.color,
    backgroundColor: theme.components.badge?.warning?.background,
    border: `1px solid ${theme.components.badge?.warning?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.warning?.color}` },
  },
  badgeUnknown: {
    color: theme.components.badge?.unknown?.color,
    backgroundColor: theme.components.badge?.unknown?.background,
    border: `1px solid ${theme.components.badge?.unknown?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.unknown?.color}` },
  },
  badgeDanger: {
    color: theme.components.badge?.danger?.color,
    backgroundColor: theme.components.badge?.danger?.background,
    border: `1px solid ${theme.components.badge?.danger?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.danger?.color}` },
  },
  badgeError: {
    color: theme.components.badge?.error?.color,
    backgroundColor: theme.components.badge?.error?.background,
    border: `1px solid ${theme.components.badge?.error?.background}`,
    '&:hover': { border: `1px solid ${theme.components.badge?.error?.color}` },
  },
  badgeTextDefault: { color: theme.components.badge?.default?.color },
  badgeTextPrimary: { color: theme.components.badge?.primary?.color },
  badgeTextSecondary: { color: theme.components.badge?.secondary?.color },
  badgeTextSuccess: { color: theme.components.badge?.success?.color },
  badgeTextWarning: { color: theme.components.badge?.warning?.color },
  badgeTextUnknown: { color: theme.components.badge?.unknown?.color },
  badgeTextDanger: { color: theme.components.badge?.danger?.color },
  badgeTextError: { color: theme.components.badge?.error?.color },
  additionalTextDefault: {
    color: theme.components.badge?.default?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.default?.background, 15),
  },
  additionalTextPrimary: {
    color: theme.components.badge?.primary?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.primary?.background, 15),
  },
  additionalTextSecondary: {
    color: theme.components.badge?.secondary?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.secondary?.background, 15),
  },
  additionalTextSuccess: {
    color: theme.components.badge?.success?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.success?.background, 15),
  },
  additionalTextWarning: {
    color: theme.components.badge?.warning?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.warning?.background, 15),
  },
  additionalTextUnknown: {
    color: theme.components.badge?.unknown?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.unknown?.background, 15),
  },
  additionalTextDanger: {
    color: theme.components.badge?.danger?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.danger?.background, 15),
  },
  additionalTextError: {
    color: theme.components.badge?.error?.color,
    backgroundColor: lightenDarkenColor(theme.components.badge?.error?.background, 15),
  },
  badgeIconContainerDefault: { color: theme.components.badge?.default?.color },
  badgeIconContainerPrimary: { color: theme.components.badge?.primary?.color },
  badgeIconContainerSecondary: { color: theme.components.badge?.secondary?.color },
  badgeIconContainerSuccess: { color: theme.components.badge?.success?.color },
  badgeIconContainerWarning: { color: theme.components.badge?.warning?.color },
  badgeIconContainerUnknown: { color: theme.components.badge?.unknown?.color },
  badgeIconContainerDanger: { color: theme.components.badge?.danger?.color },
  badgeIconContainerError: { color: theme.components.badge?.error?.color },
}))
